import React, { useEffect, useState } from 'react';
import {
  Typography, CircularProgress, Grid, Box, Button, Card, CardContent, CardMedia, Alert, IconButton, Dialog, DialogTitle, DialogContent, TextField, MenuItem, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import EditProductForm from './EditProductForm';
import AddProductsForm from './AddProductsForm';
import ImportProductsForm from './ImportProductsForm';
import { useFeatures } from "../../../../context/PermissionsAndFeatures/FeaturesContext";
import * as XLSX from 'xlsx';

const ProductList = ({ products, loading, error, refresh }) => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [filters, setFilters] = useState({ category: '', status: 'active' }); // الحالة الافتراضية غير مفعل

  // استخلاص حالة ميزة رفع ملف المينيو
  const { hasFeature, featuresLoading } = useFeatures();
  const menuUploadEnabled = !featuresLoading && hasFeature("menu_upload_excel");
  const uploadTooltip = !menuUploadEnabled ? "ميزة رفع ملف المينيو (menu_upload_excel) غير مفعّلة" : "";

  useEffect(() => {
    let temp = [...(products || [])]; // use safe array
    if (filters.category) {
      temp = temp.filter(p => p.category_name === filters.category);
    }
    if (filters.status) {
      const statusValue = filters.status === 'active' ? 1 : 0;
      temp = temp.filter(p => p.status === statusValue);
    }
    setFilteredProducts(temp);
  }, [filters, products]);

  const handleEditSuccess = () => {
    setSelectedProduct(null);
    refresh();
  };

  const handleAddSuccess = () => {
    setOpenAddDialog(false);
    refresh();
  };

  const handleImportSuccess = () => {
    setOpenImportDialog(false);
    refresh();
  };

  const handleExportToExcel = () => {
    const rows = filteredProducts.map(p => ({
      اسم_المنتج: p.name,
      السعر: p.price,
      المجموعة: p.category_name,
      الحالة: p.status === 1 ? 'مفعل' : 'غير مفعل'
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "المنتجات");
    XLSX.writeFile(workbook, "products_export.xlsx");
  };

  // تنزيل قالب ملف رفع المينيو من مجلد public/assets/templates
  const handleDownloadMenuTemplate = () => {
    // الملف موجود في public/assets/templates/RestoCashMenueUpload.xlsx
    const url = `${window.location.origin}/assets/templates/RestoCashMenueUpload.xlsx`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "RestoCashMenueUpload.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const safeProducts = products || []; // استخدم نسخة آمنة من products
  const uniqueCategories = [...new Set(safeProducts.map(p => p.category_name).filter(Boolean))];
  const totalCount = filteredProducts.length;
  const totalValue = filteredProducts.reduce((sum, p) => sum + parseFloat(p.price || 0), 0);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  // لا نعيد الفيو هنا حتى وإن كانت المصغوفة فارغة — نريد أن تبقى أزرار التحكم معروضة دائمًا

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body1">
          ✅ عدد المنتجات: {totalCount} | 💰 إجمالي القيمة: {totalValue.toFixed(2)} [جنية]
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleExportToExcel}>📤 تصدير إلى Excel</Button>
          <Button variant="outlined" onClick={handleDownloadMenuTemplate}>⬇️ تحميل ملف رفع المينيو</Button>
          <Button variant="contained" color="primary" onClick={() => setOpenAddDialog(true)} sx={{ mr: 0 }}>
            ➕ إضافة منتج جديد
          </Button>
          <Tooltip title={uploadTooltip}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => { if (menuUploadEnabled) setOpenImportDialog(true); }}
                disabled={!menuUploadEnabled}
              >
                📥 رفع ملف منتجات
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          select
          label="فلترة حسب المجموعة"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">الكل</MenuItem>
          {uniqueCategories.map((cat, idx) => (
            <MenuItem key={idx} value={cat}>{cat}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="الحالة"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">الكل</MenuItem>
          <MenuItem value="active">مفعل</MenuItem>
          <MenuItem value="inactive">غير مفعل</MenuItem>
        </TextField>
      </Box>

      <Grid container spacing={3}>
        {filteredProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {product.image && (
                <CardMedia
                  component="img"
                  height="160"
                  image={`http://moumen01.restocash.app/${product.image}`}
                  alt={product.name}
                />
              )}
              <CardContent>
                <Typography variant="h6" gutterBottom>{product.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  💰 {product.price} جنية<br />
                  📂 المجموعة: {product.category_name || 'غير محددة'}<br />
                  {product.status === 1 ? '✅ مفعل' : '🚫 غير مفعل'}<br />
                  {/* عرض الحقول الجديدة */}
                  🧾 تكلفة المنتج: {product.product_cost ?? '-'} جنية<br />
                  ⏱️ يحتاج تحضير: {(product.is_preparing == 1 || product.is_preparing === '1') ? `نعم (✳ ${product.preparing_time ?? 0} دقيقة)` : 'لا'}<br />
                </Typography>
              </CardContent>
              <Box textAlign="right" px={2} pb={2}>
                <IconButton color="primary" onClick={() => setSelectedProduct(product)}>
                  <EditIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* when no products, show friendly info but keep controls visible */}
      {(!safeProducts || safeProducts.length === 0) && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">لا توجد منتجات مضافة حتى الآن. يمكنك رفع ملف المنتجات أو إضافة منتج جديد يدوياً.</Alert>
        </Box>
      )}

      {/* show empty-state when no filtered results */}
      {filteredProducts.length === 0 && (safeProducts.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info">لا توجد منتجات تطابق عوامل التصفية الحالية.</Alert>
        </Box>
      )}

      <Dialog open={!!selectedProduct} onClose={() => setSelectedProduct(null)} maxWidth="sm" fullWidth>
        <DialogTitle>✏️ تعديل المنتج</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <EditProductForm
              initialData={selectedProduct}
              onSuccess={handleEditSuccess}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openAddDialog}
        // لا نغلق المودال عند النقر على الخلفية أو الضغط على Escape
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setOpenAddDialog(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 18 }}>➕ إضافة منتج جديد</Box>
          <IconButton
            aria-label="إغلاق المودال"
            onClick={() => setOpenAddDialog(false)}
            size="small"
            sx={{
              bgcolor: "rgba(0,0,0,0.04)",
              color: "inherit",
              "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AddProductsForm onSuccess={handleAddSuccess} />
        </DialogContent>
      </Dialog>

      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📥 استيراد منتجات من ملف</DialogTitle>
        <DialogContent>
          {/* تأكد من عدم عرض مودال الرفع إذا الميزة غير مفعلة */}
          {menuUploadEnabled ? (
            <ImportProductsForm onSuccess={handleImportSuccess} />
          ) : (
            <Typography color="error">ميزة رفع ملف المينيو غير مفعلة.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductList;
